/* eslint-disable react/prop-types */
import { Modal } from '@mui/material';
import { useStyles } from './ReuseModelsItem.styles';
import { ModalHeader, ModalVariant } from '../ModalHeader/ModalHeader';
import { CustomTypography } from '../Typography/Typography';

export interface IDosagePropertyRow {
  dosagePropertyId: string;
  dosagePropertyName: string;
  dosagePropertyDescription?: string;
  dosagePropertyRange: string;
  rangeMin: number;
  rangeMax: number;
  uom?: string;
}

export interface IDosageValueRow {
  dosagePropertyValueId?: string;
  dosageTemperaturePropertyModel: IDosagePropertyRow;
  dosageBatchAgePropertyModel: IDosagePropertyRow;
  uom?: string;
  value: number;
  isSelected?: boolean;
  IsSelected?: boolean;
}

export interface IDosageTableData {
  dosageName?: string;
  information?: string;
  data?: Array<IDosagePropertyRow | IDosageValueRow> | IDosageTableData;
  dosagePropertyValues?: Array<IDosagePropertyRow | IDosageValueRow>;
  dosageModel?: {
    dosageName?: string;
    dosagePropertyValues?: Array<IDosagePropertyRow | IDosageValueRow>;
  };
}

interface IDosageTableModalProps {
  data?: IDosageTableData | Array<IDosagePropertyRow | IDosageValueRow>;
  onClose: () => void;
}

const isPropertyArray = (
  value: unknown,
): value is Array<IDosagePropertyRow | IDosageValueRow> => Array.isArray(value);

const isDosageValueRow = (
  value: IDosagePropertyRow | IDosageValueRow,
): value is IDosageValueRow =>
  'dosageTemperaturePropertyModel' in value &&
  'dosageBatchAgePropertyModel' in value;

const getPropertyRows = (
  data?: IDosageTableData | Array<IDosagePropertyRow | IDosageValueRow>,
): IDosagePropertyRow[] => {
  if (isPropertyArray(data)) {
    return data.filter(
      (row): row is IDosagePropertyRow => !isDosageValueRow(row),
    );
  }
  const nestedData = data?.data;
  if (Array.isArray(nestedData)) {
    return nestedData.filter(
      (row): row is IDosagePropertyRow => !isDosageValueRow(row),
    );
  }
  if (nestedData && typeof nestedData === 'object') {
    return getPropertyRows(nestedData as IDosageTableData);
  }
  return (data?.dosagePropertyValues || []).filter(
    (row): row is IDosagePropertyRow => !isDosageValueRow(row),
  );
};

const getDosageValueRows = (
  data?: IDosageTableData | Array<IDosagePropertyRow | IDosageValueRow>,
): IDosageValueRow[] => {
  if (isPropertyArray(data)) {
    return data.filter(isDosageValueRow);
  }
  const nestedData = data?.data;
  if (Array.isArray(nestedData)) {
    return nestedData.filter(isDosageValueRow);
  }
  if (nestedData && typeof nestedData === 'object') {
    return getDosageValueRows(nestedData as IDosageTableData);
  }
  if (data?.dosageModel) {
    return getDosageValueRows(data.dosageModel);
  }

  return (data?.dosagePropertyValues || []).filter(isDosageValueRow);
};

const getCategoryRows = (properties: IDosagePropertyRow[], category: string) =>
  properties
    .filter(property =>
      property.dosagePropertyName
        .toLowerCase()
        .includes(category.toLowerCase()),
    )
    .sort((a, b) => a.rangeMin - b.rangeMin);

const formatRange = (property?: IDosagePropertyRow) => {
  if (!property) return '';

  return `${property.dosagePropertyRange}${
    property.uom ? ` ${property.uom}` : ''
  }`;
};

const formatPropertyName = (name?: string) => {
  if (!name) return '';

  const formattedName = name.replace(/([a-z])([A-Z])/g, '$1 $2');
  return formattedName.charAt(0).toUpperCase() + formattedName.slice(1);
};

export const DosageTableModal = ({ data, onClose }: IDosageTableModalProps) => {
  const { classes } = useStyles();
  const properties = getPropertyRows(data).filter(
    property =>
      property.dosagePropertyName.toLowerCase().includes('temperature') ||
      property.dosagePropertyName.toLowerCase().includes('batchage') ||
      property.dosagePropertyDescription
        ?.toLowerCase()
        .includes('temperature') ||
      property.dosagePropertyDescription
        ?.toLowerCase()
        .includes('concrete age'),
  );
  const dosageValueRows = getDosageValueRows(data);
  const temperatureProperties = getCategoryRows(properties, 'temperature');
  const batchAgeProperties = getCategoryRows(properties, 'batchage');
  const dosageTemperatures = dosageValueRows
    .map(row => row.dosageTemperaturePropertyModel)
    .filter(
      (property, index, rows) =>
        rows.findIndex(
          item => item.dosagePropertyId === property.dosagePropertyId,
        ) === index,
    )
    .sort((a, b) => a.rangeMin - b.rangeMin);
  const dosageBatchAges = dosageValueRows
    .map(row => row.dosageBatchAgePropertyModel)
    .filter(
      (property, index, rows) =>
        rows.findIndex(
          item => item.dosagePropertyId === property.dosagePropertyId,
        ) === index,
    )
    .sort((a, b) => a.rangeMin - b.rangeMin);
  const hasDosageValueGrid =
    dosageTemperatures.length > 0 && dosageBatchAges.length > 0;
  const hasPropertyGrid =
    temperatureProperties.length > 0 && batchAgeProperties.length > 0;
  const temperatureDescription =
    dosageTemperatures[0]?.dosagePropertyName ||
    temperatureProperties[0]?.dosagePropertyName ||
    'Temperature';
  const batchAgeDescription =
    dosageBatchAges[0]?.dosagePropertyName ||
    batchAgeProperties[0]?.dosagePropertyName ||
    'Batch age';
  const informationRows = !isPropertyArray(data)
    ? data?.information
        ?.split(';')
        .map(value => value.trim())
        .filter(Boolean)
    : [];
  const headerLabel = !isPropertyArray(data)
    ? data?.dosageName || data?.dosageModel?.dosageName || 'Dosage properties'
    : 'Dosage properties';

  return (
    <Modal open={!!data} onClose={onClose} className={classes.modal}>
      <div
        className={classes.dosageModalWrapper}
        onClick={event => event.stopPropagation()}
      >
        <ModalHeader
          variant={ModalVariant.SMALL}
          header={headerLabel}
          closeHandler={onClose}
          className={classes.dosageModalHeader}
        />
        <div className={classes.dosageTableContainer}>
          {hasDosageValueGrid ? (
            <table className={classes.dosageTable}>
              <thead>
                <tr>
                  <th className={classes.dosageCornerHeader}>
                    <span className={classes.dosageCornerTemperature}>
                      {formatPropertyName(temperatureDescription)}
                    </span>
                    <span className={classes.dosageCornerBatchAge}>
                      {formatPropertyName(batchAgeDescription)}
                    </span>
                  </th>
                  {dosageBatchAges.map(column => (
                    <th key={column.dosagePropertyId}>{formatRange(column)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dosageTemperatures.map(row => (
                  <tr key={row.dosagePropertyId}>
                    <td>{formatRange(row)}</td>
                    {dosageBatchAges.map(column => {
                      const cell = dosageValueRows.find(
                        valueRow =>
                          valueRow.dosageTemperaturePropertyModel
                            .dosagePropertyId === row.dosagePropertyId &&
                          valueRow.dosageBatchAgePropertyModel
                            .dosagePropertyId === column.dosagePropertyId,
                      );

                      return (
                        <td key={column.dosagePropertyId}>
                          <div
                            className={`${classes.dosageMatchSquare} ${
                              cell?.isSelected || cell?.IsSelected
                                ? classes.dosageSelectedCell
                                : ''
                            }`}
                          >
                            {cell
                              ? `${cell.value} ${cell.uom || ''}`.trim()
                              : ''}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : hasPropertyGrid ? (
            <table className={classes.dosageTable}>
              <thead>
                <tr>
                  <th>
                    <span className={classes.dosageCornerHeader}>
                      <span className={classes.dosageCornerTemperature}>
                        {formatPropertyName(temperatureDescription)}
                      </span>
                      <span className={classes.dosageCornerBatchAge}>
                        {formatPropertyName(batchAgeDescription)}
                      </span>
                    </span>
                  </th>
                  {batchAgeProperties.map(column => (
                    <th key={column.dosagePropertyId}>{formatRange(column)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {temperatureProperties.map(row => (
                  <tr key={row.dosagePropertyId}>
                    <td>{formatRange(row)}</td>
                    {batchAgeProperties.map(column => (
                      <td key={column.dosagePropertyId}>
                        <div className={classes.dosageMatchSquare} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className={classes.dosageTable}>
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Range</th>
                  <th>Units</th>
                </tr>
              </thead>
              <tbody>
                {properties.map(property => (
                  <tr key={property.dosagePropertyId}>
                    <td>
                      {property.dosagePropertyDescription ||
                        property.dosagePropertyName}
                    </td>
                    <td>{formatRange(property)}</td>
                    <td>{property.uom || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {informationRows && informationRows.length > 0 && (
          <div className={classes.dosageInformation}>
            {informationRows.map((value, index) => (
              <CustomTypography
                key={`${value}-${index}`}
                variant="caption2"
                color="accentDark"
              >
                {value}
                {index < informationRows.length - 1 ? ';' : ''}
              </CustomTypography>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};
